import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@deepguide-ai/ui/chart'
import { Line, LineChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import { formatDate } from '@/lib/format'
import { buildReadinessTrend } from './readiness-trend'

const trendChartConfig = {
  average: { label: 'Weekly average', color: 'var(--status-purple)' },
} satisfies ChartConfig

export function TrendChart({
  data,
  heightClass = 'h-[220px]',
}: {
  data: Array<{ overall: number; snapshotAt: string }>
  /** Container height utility — override to render a more compact chart. */
  heightClass?: string
}) {
  const chartData = buildReadinessTrend(data)
  if (chartData.length < 2) {
    return (
      <div
        className={`font-body flex ${heightClass} border-border-divider text-muted-foreground items-center justify-center rounded-sm border border-dashed text-[12px]`}
      >
        Readiness trend appears after snapshots from two different days.
        Snapshots are taken automatically.
      </div>
    )
  }

  return (
    <ChartContainer
      aria-label='Readiness trend, Weekly average'
      config={trendChartConfig}
      className={`aspect-auto ${heightClass} w-full`}
    >
      <LineChart
        accessibilityLayer
        data={chartData}
        margin={{ left: 4, right: 8, top: 8, bottom: 0 }}
      >
        <CartesianGrid
          vertical={false}
          strokeDasharray='3 3'
          stroke='var(--border)'
        />
        <XAxis
          dataKey='date'
          type='number'
          scale='time'
          domain={['dataMin', 'dataMax']}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          minTickGap={40}
          tick={{ fontSize: 10, fill: '#9A9A9A' }}
          tickFormatter={(value) =>
            new Date(value).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            })
          }
        />
        <YAxis domain={[0, 100]} hide />
        <ChartTooltip
          content={
            <ChartTooltipContent
              labelFormatter={(_, payload) =>
                formatDate(new Date(payload[0].payload.date).toISOString())
              }
              formatter={(value, _, item) => (
                <div className='grid gap-1'>
                  <span>Weekly average: {Number(value).toFixed(1)}%</span>
                  <span>Recorded score: {item.payload.overall}%</span>
                </div>
              )}
            />
          }
        />
        <Line
          dataKey='average'
          type='basis'
          stroke='var(--color-average)'
          strokeWidth={2}
          strokeLinecap='round'
          strokeLinejoin='round'
          dot={({ cx, cy, index }) =>
            index === chartData.length - 1 ? (
              <circle
                cx={cx}
                cy={cy}
                r={4}
                fill='var(--card)'
                stroke='var(--color-average)'
                strokeWidth={2}
              />
            ) : (
              <g />
            )
          }
          activeDot={{
            r: 4,
            fill: 'var(--card)',
            stroke: 'var(--color-average)',
            strokeWidth: 2,
          }}
        />
      </LineChart>
    </ChartContainer>
  )
}
